<script setup lang="ts">
import CoreButton from 'frontcore/components/CoreButton.vue'
import CoreInput from 'frontcore/views/input-text/CoreInput.vue'
import i18n from 'frontcore/i18n'
import UseCredentialsLogin from 'frontcore/domain/authorization/view/UseCredentialsLogin'
import { computed, ref, watch } from 'vue'

type Props = {
  applicationId?: string
  isFetchingUserConfiguration: boolean
}
const props = defineProps<Props>()

type Emits = {
  (e: 'error', message: string): void,
  (e: 'loginSuccess'): void,
}
const emit = defineEmits<Emits>()

const { t } = i18n.global

const username = ref<string>('')
const password = ref<string>('')

const loginUseCase = new UseCredentialsLogin(props.applicationId)
const { error, login } = loginUseCase.use()

const isLoading = ref<boolean>(false)
const loginHandler = async () => {
  isLoading.value = true
  await login(username.value, password.value)
  if (error.value !== null) {
    isLoading.value = false
    emit('error', error.value)
  } else {
    emit('loginSuccess')
  }
}

const isLoginButtonDisabled = computed<boolean>(() => {
  return username.value === '' || password.value === '' || props.isFetchingUserConfiguration
})

const isLoginButtonLoading = computed<boolean>(() => {
  return isLoading.value || (isLoading.value && props.isFetchingUserConfiguration)
})

const resetPassword = () => {
  const url = `${MPS_URL}/reset-password/?continue=${window.location}`
  window.location.assign(url)
}

watch(
  () => props.isFetchingUserConfiguration,
  (newValue, oldValue) => {
    if (newValue || newValue === oldValue) { return }
    isLoading.value = newValue
  }
)
</script>

<template>
  <form
    class="tw-flex tw-flex-col tw-gap-4 tw-items-center"
    @submit.prevent>
    <div class="tw-flex tw-flex-col tw-gap-2 tw-w-full">
      <CoreInput
        :value="username"
        :label="t('common.messages.username')"
        :placeholder="t('common.messages.username')"
        dataTest="input-username"
        @input="username = $event" />
      <CoreInput
        :value="password"
        :label="t('common.messages.password')"
        :placeholder="t('common.messages.password')"
        dataTest="input-password"
        type="password"
        @input="password = $event" />
      <a
        :class="[
          'tw-cursor-pointer body--body-01',
          'tw-text-core-primary-50 tw-decoration-core-primary-50',
          'hover:tw-underline',
          'focus:tw-shadow-sm focus:tw-shadow-core-primary-70',
          'visited:tw-text-extra-purple-30 visited:tw-decoration-extra-purple-30'
        ]"
        data-test="password-reset-link"
        @click="resetPassword">{{ t('reset_password.messages.forgot_password') }}</a>
    </div>
    <CoreButton
      :disabled="isLoginButtonDisabled"
      :label="t('common.messages.sign_in')"
      :loading="isLoginButtonLoading"
      dataTest="button-login-ok"
      type="submit"
      @click="loginHandler" />
  </form>
</template>
